import { AmountSuperScript } from "components/design/AmountSuperScript";
import { BlurFade } from "components/design/BlurFade";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Header } from "components/DesignSystem/Header/Header";
import Select from "components/DesignSystem/Select/Select";
import Tab from "components/DesignSystem/Tab/Tab";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { VendorBills } from "components/VendorBills/VendorBills";
import { VendorTransactions } from "components/Vendors/VendorTransactions";
import { YYYY_MM_DD } from "constants/date";
import { CURRENT_SEASON, VENDOR_SEASON_OPTION } from "constants/vendor";
import dayjs from "dayjs";
import { useState } from "react";
import { RowSelectionState } from "react-table-8.10.7";
import { useGetVendorDetailQuery } from "store/apis/vendors";
import { Vendor } from "types/Models/vendors";
import { useAppDispatch } from "hooks/useAppDispatch";
import { openSlider } from "store/slices/transactions";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { CategorisationHistoryTable } from "components/CategorisationHistory/CategorisationHistoryTable";
import { VendorWFormsTab } from "./VendorWFormsTab";

export const VENDOR_DETAIL_TAB = {
  TRANSACTIONS: "TRANSACTIONS",
  BILLS: "BILLS",
  CATEGORISATION_HISTORY: "CATEGORISATION_HISTORY",
  W_FORMS: "W_FORMS",
} as const;

export const VendorBillsAndTxn = ({
  entityId,
  groupId,
  vendorId,
  vendor,
}: {
  vendor: Vendor;
  entityId: string;
  groupId: string;
  vendorId: string;
}) => {
  const dispatch = useAppDispatch();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
  const [historySeason, setHistorySeason] = useState(CURRENT_SEASON);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const activeTab = query.get("tab") || VENDOR_DETAIL_TAB.TRANSACTIONS;

  const onTabChange = (value: string) => {
    update({
      query: "tab",
      value,
    });
  };

  const handleTransactionClick = (transactionId: string) => {
    dispatch(openSlider(transactionId));
  };

  const { data, isFetching, isSuccess } = useGetVendorDetailQuery(
    {
      entityId,
      vendorId,
      season: selectedSeason,
    },
    {
      skip: !vendorId || !entityId,
    }
  );

  return (
    <Tab.Root value={activeTab} onValueChange={onTabChange} className="t-mt-6">
      <Header
        v2
        bottom={
          <Tab.List>
            <Tab.Trigger value={VENDOR_DETAIL_TAB.TRANSACTIONS}>
              Transactions
            </Tab.Trigger>
            <Tab.Trigger value={VENDOR_DETAIL_TAB.BILLS}>Bills</Tab.Trigger>
            <Tab.Trigger value={VENDOR_DETAIL_TAB.CATEGORISATION_HISTORY}>
              Categorization history
            </Tab.Trigger>
            <Tab.Trigger value={VENDOR_DETAIL_TAB.W_FORMS}>W Forms</Tab.Trigger>
          </Tab.List>
        }
      />

      <div className="t-mt-5">
        <Tab.Content value={VENDOR_DETAIL_TAB.BILLS}>
          <VendorBills
            entityId={entityId}
            merchantId={vendorId}
            canRequestAndAddBill={false}
            isSearchable={false}
            setRowSelection={setRowSelection}
            rowSelection={rowSelection}
          />
        </Tab.Content>

        <Tab.Content
          value={VENDOR_DETAIL_TAB.TRANSACTIONS}
          className="t-gap-3 t-flex t-flex-col"
        >
          <div>
            <Select.Root
              size="small"
              onValueChange={setSelectedSeason}
              defaultValue={CURRENT_SEASON}
              value={selectedSeason}
            >
              <Select.Trigger>
                Season: <Select.Value placeholder="select year" />
              </Select.Trigger>
              <Select.Content sideOffset={2}>
                {VENDOR_SEASON_OPTION.map((season) => (
                  <Select.Item value={season} key={season}>
                    {season}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>

          <div className="t-flex t-gap-2 t-p-5 sm:t-px-3 t-rounded t-border-solid t-border-neutral-0 t-border t-flex-col">
            <div className="t-text-text-30 t-text-body-sm">
              Total Transacted Amount
            </div>
            <Async.Root
              isEmpty={false}
              isLoading={isFetching}
              isSuccess={isSuccess}
              customLoader={
                <div className="t-bg-neutral-10 t-rounded-md t-h-5 t-animate-pulse t-w-20" />
              }
            >
              <Async.Empty>
                <></>
              </Async.Empty>
              <Async.Success>
                <BlurFade
                  key={selectedSeason}
                  className="t-text-text-100 t-text-subtitle"
                >
                  {data && (
                    <AmountSuperScript amount={data?.transaction_amount} />
                  )}
                </BlurFade>
              </Async.Success>
            </Async.Root>
          </div>

          <VendorTransactions
            entityId={entityId}
            season={selectedSeason}
            vendorName={vendor?.name}
          />
        </Tab.Content>

        <Tab.Content
          value={VENDOR_DETAIL_TAB.CATEGORISATION_HISTORY}
          className="t-gap-3 t-flex t-flex-col"
        >
          <div>
            <Select.Root
              size="small"
              onValueChange={setHistorySeason}
              defaultValue={CURRENT_SEASON}
              value={historySeason}
            >
              <Select.Trigger>
                Season: <Select.Value placeholder="select year" />
              </Select.Trigger>
              <Select.Content sideOffset={2}>
                {VENDOR_SEASON_OPTION.map((season) => (
                  <Select.Item value={season} key={season}>
                    {season}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>

          <div className="t-pb-24">
            <CategorisationHistoryTable
              merchantId={data?.merchant_id!}
              onTransactionClick={handleTransactionClick}
              startDate={dayjs(historySeason)
                .startOf("year")
                .format(YYYY_MM_DD)}
              endDate={dayjs(historySeason).endOf("year").format(YYYY_MM_DD)}
            />
          </div>
        </Tab.Content>

        <Tab.Content value={VENDOR_DETAIL_TAB.W_FORMS}>
          <VendorWFormsTab
            entityId={entityId}
            groupId={groupId}
            vendorId={vendorId}
          />
        </Tab.Content>
      </div>

      <TransactionSlider />
    </Tab.Root>
  );
};
